import {
	Badge,
	Button,
	cn,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	Separator,
	Spinner,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from "@monbits/ui";
import {
	AlertTriangle,
	ArrowUpRight,
	Check,
	CheckCircle2,
	Copy,
	Download,
	ExternalLink,
	Loader2,
	PauseCircle,
	PlayCircle,
	RefreshCw,
	Search,
	User,
} from "lucide-react";
import * as React from "react";
import { toast } from "sonner";
import { CryptoIcon } from "src/components/crypto-icon/crypto-icon";
import { StatusBadge } from "src/components/status-badge";
import TimeLabel from "src/components/time-label/time-label";
import { BLOCKCHAIN_NAMES } from "src/constants/blockchains";
import poolWithdrawalProvider, {
	type PoolWithdrawal,
} from "src/providers/pool-withdrawal-provider";
import walletProvider, {
	type CheckDriftResponse,
	type PortfolioDetails,
	type TransactionDetails,
	type WalletDetails,
} from "src/providers/wallet-provider";
import type { AuditLog } from "src/types";
import { formatAddressShort, formatCryptoAmount } from "src/utils/format";
import { formatAuditDateTime, getCategoryConfig } from "@/utils/audit-log";

const BLOCKCHAIN_EXPLORERS: Record<
	string,
	{ name: string; addressUrl: (address: string) => string }
> = {
	ETH: {
		name: "Etherscan",
		addressUrl: (address) => `https://etherscan.io/address/${address}`,
	},
	MATIC: {
		name: "PolygonScan",
		addressUrl: (address) => `https://polygonscan.com/address/${address}`,
	},
	POLYGON: {
		name: "PolygonScan",
		addressUrl: (address) => `https://polygonscan.com/address/${address}`,
	},
	BSC: {
		name: "BscScan",
		addressUrl: (address) => `https://bscscan.com/address/${address}`,
	},
	TRON: {
		name: "TronScan",
		addressUrl: (address) => `https://tronscan.org/#/address/${address}`,
	},
	BTC: {
		name: "Blockstream",
		addressUrl: (address) => `https://blockstream.info/address/${address}`,
	},
};

const formatUsdValue = (value?: string) => {
	if (!value) return "—";
	const numeric = Number(value);
	if (Number.isNaN(numeric) || numeric === 0) return "—";
	return `$${formatCryptoAmount(value, { maxDecimals: 2 })}`;
};

interface WalletDetailsTabsProps {
	wallet: WalletDetails;
	auditLogs: AuditLog[];
	isLoadingAuditLogs: boolean;
	onBackupClick: () => void;
	onAuditLogClick: (log: AuditLog) => void;
	onWithdrawClick?: (portfolio: PortfolioDetails | null) => void;
}

export const WalletDetailsTabs: React.FC<WalletDetailsTabsProps> = ({
	wallet,
	auditLogs,
	isLoadingAuditLogs,
	onBackupClick,
	onAuditLogClick,
	onWithdrawClick,
}) => {
	const [activeTab, setActiveTab] = React.useState("overview");
	const [copiedField, setCopiedField] = React.useState<string | null>(null);
	const [isSuspending, setIsSuspending] = React.useState(false);
	const [suspensionState, setSuspensionState] = React.useState<{
		suspendedAt: string | null;
		suspendedReason: string;
	}>({
		suspendedAt: wallet.transferSuspendedAt ?? null,
		suspendedReason: wallet.transferSuspendedReason ?? "",
	});

	// Keep local suspension state in sync if the parent re-fetches the wallet
	// (e.g. the scheduler suspended it while the panel was open).
	React.useEffect(() => {
		setSuspensionState({
			suspendedAt: wallet.transferSuspendedAt ?? null,
			suspendedReason: wallet.transferSuspendedReason ?? "",
		});
	}, [wallet.transferSuspendedAt, wallet.transferSuspendedReason]);

	type ResumeDialogState =
		| { step: "closed" }
		| { step: "checking" }
		| { step: "results"; data: CheckDriftResponse }
		| { step: "reconciling"; data: CheckDriftResponse }
		| { step: "error"; message: string };

	const [resumeDialog, setResumeDialog] = React.useState<ResumeDialogState>({
		step: "closed",
	});

	const handleSuspend = React.useCallback(async () => {
		try {
			setIsSuspending(true);
			await walletProvider.suspendWalletTransfers(
				wallet.id,
				"Manually suspended by admin",
			);
			setSuspensionState({
				suspendedAt: new Date().toISOString(),
				suspendedReason: "Manually suspended by admin",
			});
			toast.success("Wallet transfers suspended");
		} catch (error) {
			console.error("Failed to suspend:", error);
			toast.error("Failed to suspend wallet transfers");
		} finally {
			setIsSuspending(false);
		}
	}, [wallet.id]);

	const handleOpenResumeDialog = React.useCallback(async () => {
		setResumeDialog({ step: "checking" });
		try {
			const result = await walletProvider.checkBalanceDrift(wallet.id);
			setResumeDialog({ step: "results", data: result });
		} catch (error) {
			const message = error instanceof Error ? error.message : "Unknown error";
			setResumeDialog({ step: "error", message });
		}
	}, [wallet.id]);

	const handleReconcileAndResume = React.useCallback(async () => {
		if (resumeDialog.step !== "results") return;
		setResumeDialog({ step: "reconciling", data: resumeDialog.data });
		try {
			const result = await walletProvider.reconcileAndResume(wallet.id);
			const adjCount = result.adjustments.length;
			setResumeDialog({ step: "closed" });
			setSuspensionState({ suspendedAt: null, suspendedReason: "" });
			toast.success("Wallet resumed", {
				description:
					adjCount > 0
						? `${adjCount} balance adjustment${adjCount !== 1 ? "s" : ""} applied.`
						: "No adjustments needed. Transfers will resume on the next scheduler run.",
			});
		} catch (error) {
			const message = error instanceof Error ? error.message : "Unknown error";
			setResumeDialog({ step: "error", message });
		}
	}, [wallet.id, resumeDialog]);
	const [withdrawals, setWithdrawals] = React.useState<PoolWithdrawal[]>([]);
	const [isLoadingWithdrawals, setIsLoadingWithdrawals] = React.useState(false);
	const [portfolio, setPortfolio] = React.useState<PortfolioDetails | null>(
		wallet.portfolio || null,
	);
	const [isRefreshingPortfolio, setIsRefreshingPortfolio] =
		React.useState(false);
	const [transactions, setTransactions] = React.useState<TransactionDetails[]>(
		[],
	);
	const [transactionsTotalCount, setTransactionsTotalCount] = React.useState(0);
	const [transactionsPage, setTransactionsPage] = React.useState(1);
	const [isLoadingTransactions, setIsLoadingTransactions] =
		React.useState(false);

	const transactionsPerPage = 20;

	const copyToClipboard = async (text: string, fieldName: string) => {
		try {
			await navigator.clipboard.writeText(text);
			setCopiedField(fieldName);
			toast.success(`${fieldName} copied to clipboard`);
			setTimeout(() => setCopiedField(null), 2000);
		} catch (_error) {
			toast.error("Failed to copy to clipboard");
		}
	};

	const loadWithdrawals = React.useCallback(async () => {
		try {
			setIsLoadingWithdrawals(true);
			const response = await poolWithdrawalProvider.listWithdrawals({
				outboundWalletId: wallet.id,
				limit: 50,
			});
			setWithdrawals(response.withdrawals);
		} catch (error) {
			console.error("Failed to load withdrawals:", error);
			toast.error("Failed to load withdrawals");
		} finally {
			setIsLoadingWithdrawals(false);
		}
	}, [wallet.id]);

	const loadTransactions = React.useCallback(
		async (page: number) => {
			try {
				setIsLoadingTransactions(true);
				const offset = (page - 1) * transactionsPerPage;
				const response = await walletProvider.getWalletTransactions(wallet.id, {
					limit: transactionsPerPage,
					offset,
				});
				setTransactions(response.transactions);
				setTransactionsTotalCount(response.pagination.totalCount);
			} catch (error) {
				console.error("Failed to load transactions:", error);
				toast.error("Failed to load transactions");
			} finally {
				setIsLoadingTransactions(false);
			}
		},
		[wallet.id],
	);

	const loadPortfolio = React.useCallback(async () => {
		try {
			setIsRefreshingPortfolio(true);
			// Always fetch fresh data from blockchain when opening wallet
			const freshPortfolio = await walletProvider.refreshWalletPortfolio(
				wallet.id,
			);
			setPortfolio(freshPortfolio);
		} catch (error) {
			console.error("Failed to load portfolio:", error);
			// Try to fall back to cached data
			try {
				const cachedPortfolio = await walletProvider.getWalletPortfolio(
					wallet.id,
				);
				setPortfolio(cachedPortfolio);
			} catch {
				// Ignore fallback errors
			}
		} finally {
			setIsRefreshingPortfolio(false);
		}
	}, [wallet.id]);

	const refreshPortfolio = React.useCallback(async () => {
		try {
			setIsRefreshingPortfolio(true);
			const freshPortfolio = await walletProvider.refreshWalletPortfolio(
				wallet.id,
			);
			setPortfolio(freshPortfolio);
			toast.success("Portfolio refreshed");
		} catch (error) {
			console.error("Failed to refresh portfolio:", error);
			toast.error("Failed to refresh portfolio");
		} finally {
			setIsRefreshingPortfolio(false);
		}
	}, [wallet.id]);

	// Load portfolio from blockchain when wallet details opens
	React.useEffect(() => {
		loadPortfolio();
	}, [loadPortfolio]);

	React.useEffect(() => {
		if (activeTab === "withdrawals" && wallet.type === "outbound") {
			loadWithdrawals();
		}
	}, [activeTab, wallet.type, loadWithdrawals]);

	React.useEffect(() => {
		if (activeTab === "transactions") {
			loadTransactions(transactionsPage);
		}
	}, [activeTab, transactionsPage, loadTransactions]);

	return (
		<div className="space-y-4">
			<div className="space-y-4 pb-4 border-b">
				{/* Header - stacks on mobile */}
				<div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
					<div className="flex items-center gap-3">
						<CryptoIcon
							ticker={wallet.blockchain}
							className="h-10 w-10 flex-shrink-0"
						/>
						<div className="min-w-0">
							<h3 className="font-semibold text-lg">
								{BLOCKCHAIN_NAMES[wallet.blockchain] || wallet.blockchain}{" "}
								Wallet
							</h3>
							<div className="flex items-center gap-2 mt-1">
								<Badge variant="outline" className="capitalize">
									{wallet.type}
								</Badge>
								{wallet.blockedAt && (
									<Badge variant="destructive" className="gap-1">
										<AlertTriangle className="h-3 w-3" />
										Blocked
									</Badge>
								)}
								{suspensionState.suspendedAt && (
									<Badge
										variant="secondary"
										className="gap-1 border-amber-500/50 text-amber-600 dark:text-amber-400"
									>
										<PauseCircle className="h-3 w-3" />
										Transfer Suspended
									</Badge>
								)}
							</div>
						</div>
					</div>
					<div className="flex gap-2 w-full sm:w-auto flex-wrap">
						{wallet.type === "inbound" && !suspensionState.suspendedAt && (
							<Button
								variant="outline"
								size="sm"
								onClick={handleSuspend}
								disabled={isSuspending}
								className="flex-1 sm:flex-none text-amber-600 border-amber-500/50 hover:bg-amber-50 dark:hover:bg-amber-950/30"
							>
								{isSuspending ? (
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								) : (
									<PauseCircle className="mr-2 h-4 w-4" />
								)}
								Suspend Transfers
							</Button>
						)}
						{wallet.type === "inbound" && suspensionState.suspendedAt && (
							<Button
								variant="outline"
								size="sm"
								onClick={handleOpenResumeDialog}
								className="flex-1 sm:flex-none"
							>
								<Search className="mr-2 h-4 w-4" />
								Check &amp; Resume
							</Button>
						)}
						{wallet.type === "outbound" && onWithdrawClick && (
							<Button
								variant="outline"
								size="sm"
								onClick={() => onWithdrawClick(portfolio)}
								disabled={isRefreshingPortfolio}
								className="flex-1 sm:flex-none"
							>
								{isRefreshingPortfolio ? (
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								) : (
									<ArrowUpRight className="mr-2 h-4 w-4" />
								)}
								<span className="hidden xs:inline">
									{isRefreshingPortfolio ? "Loading..." : "Withdraw"}
								</span>
								<span className="xs:hidden">
									{isRefreshingPortfolio ? "..." : "Withdraw"}
								</span>
							</Button>
						)}
						<Button
							variant="outline"
							size="sm"
							onClick={onBackupClick}
							className="flex-1 sm:flex-none"
						>
							<Download className="mr-2 h-4 w-4" />
							Backup
						</Button>
					</div>
				</div>

				{/* Wallet Info Card - responsive grid */}
				<div className="grid gap-2 p-3 sm:p-4 bg-muted/50 rounded-lg border text-sm">
					<div className="flex justify-between items-center">
						<span className="text-muted-foreground">ID</span>
						<span className="font-mono">{wallet.id}</span>
					</div>

					<div className="flex justify-between items-center gap-2 min-w-0">
						<span className="text-muted-foreground flex-shrink-0">Address</span>
						<div className="flex items-center gap-1 min-w-0">
							<span className="font-mono truncate">{wallet.address}</span>
							<Button
								variant="ghost"
								size="sm"
								className="h-6 w-6 p-0 flex-shrink-0"
								onClick={() => copyToClipboard(wallet.address, "Address")}
								title="Copy address"
							>
								{copiedField === "Address" ? (
									<Check className="h-3 w-3 text-green-600" />
								) : (
									<Copy className="h-3 w-3" />
								)}
							</Button>
							{BLOCKCHAIN_EXPLORERS[wallet.blockchain] && (
								<Button
									variant="ghost"
									size="sm"
									className="h-6 w-6 p-0 flex-shrink-0"
									onClick={() =>
										window.open(
											BLOCKCHAIN_EXPLORERS[wallet.blockchain].addressUrl(
												wallet.address,
											),
											"_blank",
										)
									}
									title={`View on ${BLOCKCHAIN_EXPLORERS[wallet.blockchain].name}`}
								>
									<ExternalLink className="h-3 w-3" />
								</Button>
							)}
						</div>
					</div>

					<div className="flex justify-between items-center">
						<span className="text-muted-foreground">Created</span>
						<TimeLabel time={wallet.createdAt} />
					</div>
				</div>

				{wallet.blockedAt && (
					<div className="p-4 bg-red-50 dark:bg-red-950/30 rounded-lg border border-red-200 dark:border-red-900">
						<div className="flex items-center gap-2 text-red-700 dark:text-red-400 mb-2">
							<AlertTriangle className="h-4 w-4" />
							<span className="font-semibold text-sm">Wallet Blocked</span>
						</div>
						<div className="space-y-1 text-sm">
							<div className="flex gap-2">
								<span className="text-red-600/70 dark:text-red-400/70">
									Blocked:
								</span>
								<span className="text-red-700 dark:text-red-300">
									<TimeLabel time={wallet.blockedAt} />
								</span>
							</div>
							{wallet.blockedReason && (
								<div className="flex gap-2">
									<span className="text-red-600/70 dark:text-red-400/70">
										Reason:
									</span>
									<span className="text-red-700 dark:text-red-300">
										{wallet.blockedReason}
									</span>
								</div>
							)}
						</div>
					</div>
				)}

				{suspensionState.suspendedAt && (
					<div className="p-4 bg-amber-50 dark:bg-amber-950/20 rounded-lg border border-amber-200/60 dark:border-amber-900/40">
						<div className="flex items-center gap-2 text-amber-700 dark:text-amber-400 mb-2">
							<PauseCircle className="h-4 w-4" />
							<span className="font-semibold text-sm">Transfers Suspended</span>
						</div>
						<div className="space-y-1 text-sm">
							<div className="flex gap-2">
								<span className="text-amber-600/70 dark:text-amber-400/70">
									Since:
								</span>
								<span className="text-amber-700 dark:text-amber-300">
									<TimeLabel time={suspensionState.suspendedAt} />
								</span>
							</div>
							{suspensionState.suspendedReason && (
								<div className="flex gap-2">
									<span className="text-amber-600/70 dark:text-amber-400/70">
										Reason:
									</span>
									<span className="text-amber-700 dark:text-amber-300">
										{suspensionState.suspendedReason}
									</span>
								</div>
							)}
						</div>
					</div>
				)}

				{suspensionState.suspendedAt && (
					<ResumeTransfersDialog
						state={resumeDialog}
						blockchain={wallet.blockchain}
						onClose={() => setResumeDialog({ step: "closed" })}
						onRetry={handleOpenResumeDialog}
						onReconcileAndResume={handleReconcileAndResume}
					/>
				)}
			</div>

			<Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
				<TabsList className="w-full flex overflow-x-auto no-scrollbar">
					<TabsTrigger value="overview" className="flex-1 min-w-fit px-3">
						Overview
					</TabsTrigger>
					<TabsTrigger value="transactions" className="flex-1 min-w-fit px-3">
						<span className="hidden sm:inline">Transactions</span>
						<span className="sm:hidden">Txns</span>
						<span className="ml-1 text-xs opacity-70">
							({transactionsTotalCount})
						</span>
					</TabsTrigger>
					{wallet.type === "outbound" && (
						<TabsTrigger value="withdrawals" className="flex-1 min-w-fit px-3">
							<span className="hidden sm:inline">Withdrawals</span>
							<span className="sm:hidden">Wdrw</span>
							<span className="ml-1 text-xs opacity-70">
								({withdrawals?.length ?? 0})
							</span>
						</TabsTrigger>
					)}
					<TabsTrigger value="audit" className="flex-1 min-w-fit px-3">
						<span className="hidden sm:inline">Audit Logs</span>
						<span className="sm:hidden">Audit</span>
						<span className="ml-1 text-xs opacity-70">
							({auditLogs.length})
						</span>
					</TabsTrigger>
				</TabsList>

				<TabsContent value="overview" className="space-y-4 mt-4">
					{portfolio && !portfolio.error && (
						<div className="space-y-4">
							{/* Total Value Card */}
							<div className="p-4 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent rounded-xl border">
								<div className="flex items-center justify-between mb-2">
									<div className="flex items-center gap-2">
										<h4 className="font-semibold text-sm text-muted-foreground">
											Total Portfolio Value
										</h4>
										{portfolio.source === "database" && (
											<Badge variant="outline" className="text-xs">
												Cached
											</Badge>
										)}
										{portfolio.source === "blockchain" && (
											<Badge
												variant="outline"
												className="text-xs text-green-600 border-green-600/30"
											>
												Live
											</Badge>
										)}
									</div>
									<Button
										variant="ghost"
										size="sm"
										onClick={refreshPortfolio}
										disabled={isRefreshingPortfolio}
										className="h-8 px-2"
									>
										{isRefreshingPortfolio ? (
											<Loader2 className="h-4 w-4 animate-spin" />
										) : (
											<RefreshCw className="h-4 w-4" />
										)}
									</Button>
								</div>
								<p className="text-2xl font-bold">
									{formatUsdValue(portfolio.totalValueUsd)}
								</p>
							</div>

							{/* Assets List */}
							<div className="space-y-2">
								<h4 className="font-semibold text-sm text-muted-foreground px-1">
									Assets
								</h4>
								{portfolio.native && (
									<div className="flex justify-between items-center p-3 border rounded-lg hover:bg-muted/30 transition-colors">
										<div className="flex items-center gap-3">
											<div className="relative">
												<CryptoIcon
													ticker={portfolio.native.symbol}
													className="h-8 w-8"
												/>
											</div>
											<div>
												<p className="font-semibold text-sm">
													{portfolio.native.symbol}
												</p>
												<p className="text-xs text-muted-foreground">
													Native Coin
												</p>
											</div>
										</div>
										<div className="text-right">
											<p className="font-semibold text-sm">
												{formatCryptoAmount(portfolio.native.balance, {
													ticker: portfolio.native.symbol,
													maxDecimals: 6,
												})}{" "}
												<span className="text-muted-foreground font-normal">
													{portfolio.native.symbol}
												</span>
											</p>
											<p className="text-sm text-muted-foreground">
												{formatUsdValue(portfolio.native.valueUsd)}
											</p>
										</div>
									</div>
								)}
								{portfolio.tokens.map((token, idx) => {
									const ticker = `${wallet.blockchain}_${token.symbol}`;
									return (
										<div
											key={idx}
											className="flex justify-between items-center p-3 border rounded-lg hover:bg-muted/30 transition-colors"
										>
											<div className="flex items-center gap-3">
												<CryptoIcon
													ticker={ticker}
													showComposite
													className="h-8 w-8"
												/>
												<div>
													<p className="font-semibold text-sm">
														{token.symbol}
													</p>
													<p className="text-xs text-muted-foreground font-mono">
														{formatAddressShort(token.address, {
															head: 6,
															tail: 4,
														})}
													</p>
												</div>
											</div>
											<div className="text-right">
												<p className="font-semibold text-sm">
													{formatCryptoAmount(token.balance, {
														ticker,
														maxDecimals: 2,
													})}{" "}
													<span className="text-muted-foreground font-normal">
														{token.symbol}
													</span>
												</p>
												<p className="text-sm text-muted-foreground">
													{formatUsdValue(token.valueUsd)}
												</p>
											</div>
										</div>
									);
								})}
								{!portfolio.native && portfolio.tokens.length === 0 && (
									<div className="text-center py-8 text-muted-foreground text-sm">
										No assets found in this wallet
									</div>
								)}
							</div>
						</div>
					)}

					{wallet.currentLock && (
						<>
							<Separator />
							<div className="space-y-3">
								<h4 className="font-semibold">Current Lock</h4>
								<div className="p-3 border rounded-lg space-y-2 text-sm">
									<div className="flex justify-between">
										<span className="text-muted-foreground">Merchant ID</span>
										<span className="font-mono">
											{wallet.currentLock.merchantId}
										</span>
									</div>
									<div className="flex justify-between">
										<span className="text-muted-foreground">Currency</span>
										<span>{wallet.currentLock.currency}</span>
									</div>
									<div className="flex justify-between">
										<span className="text-muted-foreground">Network ID</span>
										<span className="font-mono">
											{wallet.currentLock.networkId}
										</span>
									</div>
									<div className="flex justify-between">
										<span className="text-muted-foreground">Locked At</span>
										<TimeLabel time={wallet.currentLock.lockedAt} />
									</div>
								</div>
							</div>
						</>
					)}
				</TabsContent>

				<TabsContent value="transactions" className="space-y-3 mt-4">
					{isLoadingTransactions ? (
						<div className="flex justify-center py-12">
							<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
						</div>
					) : !transactions || transactions.length === 0 ? (
						<div className="text-center py-12 text-muted-foreground text-sm">
							No transactions yet
						</div>
					) : (
						<>
							<div className="space-y-2">
								{transactions.map((tx) => {
									const isIncoming = tx.direction === "incoming";
									return (
										<div
											key={tx.id}
											className={cn(
												"p-3 border rounded-lg hover:bg-muted/50 transition-colors",
												isIncoming
													? "border-l-2 border-l-green-500"
													: "border-l-2 border-l-orange-500",
											)}
										>
											{/* Main row: Amount + Status */}
											<div className="flex items-center justify-between gap-2">
												<div className="flex items-center gap-2 min-w-0">
													<CryptoIcon
														ticker={`${wallet.blockchain}_${tx.currency}`}
														showComposite
														className="h-5 w-5 flex-shrink-0"
													/>
													<div className="min-w-0">
														<span
															className={cn(
																"font-semibold font-mono text-sm",
																isIncoming
																	? "text-green-600 dark:text-green-600"
																	: "",
															)}
														>
															{isIncoming ? "+" : "-"}
															{formatCryptoAmount(tx.amount, {
																maxDecimals: 6,
															})}{" "}
															<span className="text-foreground font-medium">
																{tx.currency}
															</span>
														</span>
													</div>
												</div>
												<StatusBadge
													type={
														tx.status === "completed"
															? "success"
															: tx.status === "failed" ||
																	tx.status === "canceled"
																? "error"
																: tx.status === "pending"
																	? "warning"
																	: "info"
													}
													className="text-xs flex-shrink-0"
												>
													{tx.status}
												</StatusBadge>
											</div>

											{/* Second row: Type, USD value, Time */}
											<div className="flex items-center justify-between gap-2 mt-1.5 text-xs text-muted-foreground">
												<div className="flex items-center gap-2">
													<span className="capitalize">
														{tx.type.replace("_", " ")}
													</span>
													<span>•</span>
													<span>{formatUsdValue(tx.usdAmount)}</span>
												</div>
												<TimeLabel time={tx.createdAt} />
											</div>

											{/* Third row: Hash (if available) */}
											{tx.hash && (
												<div className="flex items-center gap-1 mt-1.5">
													<p className="text-xs text-muted-foreground font-mono truncate flex-1">
														{formatAddressShort(tx.hash, { head: 10, tail: 8 })}
													</p>
													<Button
														variant="ghost"
														size="icon"
														className="h-5 w-5 flex-shrink-0"
														onClick={(e) => {
															e.stopPropagation();
															if (tx.hash) {
																copyToClipboard(tx.hash, "Hash");
															}
														}}
														title="Copy hash"
													>
														{copiedField === "Hash" ? (
															<Check className="h-3 w-3 text-green-600" />
														) : (
															<Copy className="h-3 w-3" />
														)}
													</Button>
													{tx.explorerLink && (
														<Button
															variant="ghost"
															size="icon"
															className="h-5 w-5 flex-shrink-0"
															onClick={(e) => {
																e.stopPropagation();
																window.open(tx.explorerLink, "_blank");
															}}
															title="View on explorer"
														>
															<ExternalLink className="h-3 w-3" />
														</Button>
													)}
												</div>
											)}
										</div>
									);
								})}
							</div>
							{transactionsTotalCount > transactionsPerPage && (
								<div className="flex flex-col sm:flex-row items-center justify-between gap-2 px-2 pt-2">
									<div className="text-xs sm:text-sm text-muted-foreground text-center sm:text-left">
										<span className="hidden sm:inline">Showing </span>
										{(transactionsPage - 1) * transactionsPerPage + 1}-
										{Math.min(
											transactionsPage * transactionsPerPage,
											transactionsTotalCount,
										)}
										{" of "}
										{transactionsTotalCount}
									</div>
									<div className="flex gap-2">
										<Button
											variant="outline"
											size="sm"
											onClick={() =>
												setTransactionsPage((p) => Math.max(1, p - 1))
											}
											disabled={transactionsPage === 1}
										>
											Prev
										</Button>
										<Button
											variant="outline"
											size="sm"
											onClick={() =>
												setTransactionsPage((p) =>
													Math.min(
														Math.ceil(
															transactionsTotalCount / transactionsPerPage,
														),
														p + 1,
													),
												)
											}
											disabled={
												transactionsPage ===
												Math.ceil(transactionsTotalCount / transactionsPerPage)
											}
										>
											Next
										</Button>
									</div>
								</div>
							)}
						</>
					)}
				</TabsContent>

				{wallet.type === "outbound" && (
					<TabsContent value="withdrawals" className="space-y-3 mt-4">
						{isLoadingWithdrawals ? (
							<div className="flex justify-center py-12">
								<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
							</div>
						) : !withdrawals || withdrawals.length === 0 ? (
							<div className="text-center py-12 text-muted-foreground text-sm">
								No withdrawals yet
							</div>
						) : (
							<div className="space-y-2">
								{withdrawals.map((withdrawal) => (
									<div
										key={withdrawal.id}
										className="p-3 border rounded-lg hover:bg-muted/50 transition-colors"
									>
										{/* Main row: Amount + Status */}
										<div className="flex items-center justify-between gap-2">
											<div className="flex items-center gap-2 min-w-0">
												<CryptoIcon
													ticker={`${withdrawal.blockchain}_${withdrawal.currency}`}
													showComposite
													className="h-5 w-5 flex-shrink-0"
												/>
												<span className="font-semibold font-mono text-sm">
													-
													{formatCryptoAmount(withdrawal.amount, {
														maxDecimals: 6,
													})}{" "}
													<span className="font-medium">
														{withdrawal.currency}
													</span>
												</span>
											</div>
											<StatusBadge
												type={
													withdrawal.status === "completed"
														? "success"
														: withdrawal.status === "failed" ||
																withdrawal.status === "rejected" ||
																withdrawal.status === "expired"
															? "error"
															: withdrawal.status === "awaiting_approval"
																? "warning"
																: "info"
												}
												className="text-xs flex-shrink-0"
											>
												{withdrawal.status.replace("_", " ")}
											</StatusBadge>
										</div>

										{/* Second row: USD value + Time */}
										<div className="flex items-center justify-between gap-2 mt-1.5 text-xs text-muted-foreground">
											<span>{formatUsdValue(withdrawal.usdAmount)}</span>
											<TimeLabel time={withdrawal.createdAt} />
										</div>

										{/* Third row: Destination */}
										<div className="flex items-center gap-1 mt-1.5">
											<span className="text-xs text-muted-foreground">To:</span>
											<span className="text-xs font-mono text-muted-foreground truncate flex-1">
												{formatAddressShort(withdrawal.recipientAddress, {
													head: 10,
													tail: 8,
												})}
											</span>
											<Button
												variant="ghost"
												size="icon"
												className="h-5 w-5 flex-shrink-0"
												onClick={(e) => {
													e.stopPropagation();
													copyToClipboard(
														withdrawal.recipientAddress,
														"Address",
													);
												}}
												title="Copy address"
											>
												{copiedField === "Address" ? (
													<Check className="h-3 w-3 text-green-600" />
												) : (
													<Copy className="h-3 w-3" />
												)}
											</Button>
										</div>
									</div>
								))}
							</div>
						)}
					</TabsContent>
				)}

				<TabsContent value="audit" className="space-y-3 mt-4">
					{isLoadingAuditLogs ? (
						<div className="flex justify-center py-12">
							<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
						</div>
					) : auditLogs.length === 0 ? (
						<div className="text-center py-12 text-muted-foreground text-sm">
							No audit logs yet
						</div>
					) : (
						<div className="space-y-2">
							{auditLogs.map((log) => {
								const config = getCategoryConfig(log.actionCategory);
								const Icon = config.icon;

								return (
									<div
										key={log.id}
										className="p-3 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
										onClick={() => onAuditLogClick(log)}
									>
										<div className="flex gap-3">
											<div
												className="h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0"
												style={{
													backgroundColor: `${config.color}15`,
													color: config.color,
												}}
											>
												<Icon className="h-4 w-4" />
											</div>
											<div className="flex-1 min-w-0 space-y-1">
												<div className="flex items-center gap-2 min-w-0">
													<User className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
													<span className="font-medium text-sm truncate">
														{log.actor.email ||
															`${log.actor.type} #${log.actor.id}`}
													</span>
													<span className="text-muted-foreground">•</span>
													<span className="text-sm text-muted-foreground flex-shrink-0">
														{formatAuditDateTime(log.createdAt)}
													</span>
												</div>
												<p className="text-xs text-muted-foreground leading-relaxed">
													{log.description}
												</p>
												{log.metadata &&
													typeof log.metadata.error === "string" &&
													log.metadata.error && (
														<p className="text-xs text-red-600 leading-relaxed">
															Error: {log.metadata.error}
														</p>
													)}
											</div>
										</div>
									</div>
								);
							})}
						</div>
					)}
				</TabsContent>
			</Tabs>
		</div>
	);
};

function ResumeTransfersDialog({
	state,
	blockchain,
	onClose,
	onRetry,
	onReconcileAndResume,
}: {
	state:
		| { step: "closed" }
		| { step: "checking" }
		| { step: "results"; data: CheckDriftResponse }
		| { step: "reconciling"; data: CheckDriftResponse }
		| { step: "error"; message: string };
	blockchain: string;
	onClose: () => void;
	onRetry: () => void;
	onReconcileAndResume: () => void;
}) {
	const isOpen = state.step !== "closed";
	const isLoading = state.step === "checking" || state.step === "reconciling";

	return (
		<Dialog
			open={isOpen}
			onOpenChange={(open) => {
				if (!open && !isLoading) onClose();
			}}
		>
			<DialogContent className="sm:max-w-lg">
				{state.step === "checking" && (
					<>
						<DialogHeader>
							<DialogTitle>Checking balance drift</DialogTitle>
							<DialogDescription>
								Comparing ledger balances against on-chain values...
							</DialogDescription>
						</DialogHeader>
						<div className="flex items-center justify-center py-8">
							<Spinner className="size-6 text-muted-foreground" />
						</div>
					</>
				)}

				{state.step === "results" &&
					(() => {
						const driftResults = state.data.results.filter(
							(r) => r.hasDrift || r.error,
						);
						return (
							<>
								<DialogHeader>
									<DialogTitle>
										{state.data.hasDrift
											? "Balance drift detected"
											: "Balances in sync"}
									</DialogTitle>
									<DialogDescription>
										{state.data.hasDrift
											? `${driftResults.length} ${driftResults.length === 1 ? "currency has" : "currencies have"} drifted. Reconciliation will adjust the ledger to match on-chain values.`
											: `All balances for this ${blockchain} wallet match on-chain values. Safe to resume.`}
									</DialogDescription>
								</DialogHeader>

								{state.data.hasDrift ? (
									<div className="my-4 rounded-lg border overflow-hidden">
										<table className="w-full text-sm">
											<thead>
												<tr className="border-b bg-muted/40">
													<th className="px-3 py-2 text-left font-medium text-muted-foreground">
														Currency
													</th>
													<th className="px-3 py-2 text-right font-medium text-muted-foreground">
														Ledger
													</th>
													<th className="px-3 py-2 text-right font-medium text-muted-foreground">
														On-chain
													</th>
													<th className="px-3 py-2 text-right font-medium text-muted-foreground">
														Diff
													</th>
												</tr>
											</thead>
											<tbody>
												{driftResults.map((r) => (
													<tr
														key={`${r.currency}-${r.networkId}`}
														className="border-b last:border-0"
													>
														<td className="px-3 py-2 font-mono font-medium">
															{r.currency}
														</td>
														<td className="px-3 py-2 text-right font-mono tabular-nums">
															{r.error ? (
																<span className="text-muted-foreground">
																	&mdash;
																</span>
															) : (
																r.dbAmount
															)}
														</td>
														<td className="px-3 py-2 text-right font-mono tabular-nums">
															{r.error ? (
																<span className="text-muted-foreground">
																	&mdash;
																</span>
															) : (
																r.chainAmount
															)}
														</td>
														<td className="px-3 py-2 text-right font-mono tabular-nums">
															{r.error ? (
																<span
																	className="text-destructive cursor-help"
																	title={r.error}
																>
																	Error
																</span>
															) : (
																<span className="text-amber-600 dark:text-amber-400">
																	{r.diff}
																</span>
															)}
														</td>
													</tr>
												))}
											</tbody>
										</table>
									</div>
								) : (
									<div className="flex items-center gap-2 py-6 justify-center text-sm text-green-600 dark:text-green-400">
										<CheckCircle2 className="size-5" />
										<span>No adjustments needed</span>
									</div>
								)}

								<DialogFooter>
									<Button variant="outline" onClick={onClose}>
										Cancel
									</Button>
									<Button onClick={onReconcileAndResume}>
										<PlayCircle className="mr-1.5 size-4" />
										{state.data.hasDrift
											? "Reconcile & Resume"
											: "Resume Transfers"}
									</Button>
								</DialogFooter>
							</>
						);
					})()}

				{state.step === "reconciling" && (
					<>
						<DialogHeader>
							<DialogTitle>
								{state.data.hasDrift
									? "Reconciling balances"
									: "Resuming transfers"}
							</DialogTitle>
							<DialogDescription>
								{state.data.hasDrift
									? "Adjusting ledger balances and resuming transfers..."
									: "Resuming automatic internal transfers..."}
							</DialogDescription>
						</DialogHeader>
						<div className="flex items-center justify-center py-8">
							<Spinner className="size-6 text-muted-foreground" />
						</div>
					</>
				)}

				{state.step === "error" && (
					<>
						<DialogHeader>
							<div className="flex items-center gap-3">
								<div className="flex size-10 items-center justify-center rounded-full bg-destructive/10">
									<AlertTriangle className="size-5 text-destructive" />
								</div>
								<DialogTitle>Something went wrong</DialogTitle>
							</div>
							<DialogDescription>{state.message}</DialogDescription>
						</DialogHeader>
						<DialogFooter className="mt-4">
							<Button variant="outline" onClick={onClose}>
								Close
							</Button>
							<Button variant="outline" onClick={onRetry}>
								Retry
							</Button>
						</DialogFooter>
					</>
				)}
			</DialogContent>
		</Dialog>
	);
}
